import { useCallback, useEffect, useState } from 'react';
import type { ShiftHandoffBoard } from './ShiftHandoffPanel.js';
import { UiIcon } from './UiIcon.js';

export type BoardOperation = ShiftHandoffBoard['operations'][number];
export type BoardFilter = 'all' | 'today' | 'attention' | 'in_progress';
const states: Readonly<Record<string, string>> = {
  draft: 'Rascunho',
  planned: 'Planejada',
  coordinating: 'Em coordenação',
  ready: 'Coordenação concluída',
  in_progress: 'Em execução',
  completed: 'Execução concluída',
  closed: 'Encerrada',
  cancelled: 'Cancelada',
};
export function isSameLocalDay(value: string, now: Date) {
  const date = new Date(value);
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}
export function selectBoardOperations(
  operations: readonly BoardOperation[],
  filter: BoardFilter,
  query: string,
  now: Date,
): BoardOperation[] {
  const search = query.trim().toLocaleLowerCase('pt-BR');
  return operations
    .filter((operation) => {
      if (
        filter === 'today' &&
        (!operation.nextEventAt || !isSameLocalDay(operation.nextEventAt, now))
      )
        return false;
      if (filter === 'attention' && operation.pendingItems.length === 0) return false;
      if (filter === 'in_progress' && operation.state !== 'in_progress') return false;
      return (
        !search ||
        [
          operation.referenceCode,
          operation.missionName,
          operation.registrationMark,
          operation.nextOrigin,
          operation.nextDestination,
          operation.responsible?.name,
        ].some((value) => value?.toLocaleLowerCase('pt-BR').includes(search))
      );
    })
    .toSorted(
      (a, b) =>
        (a.nextEventAt ? new Date(a.nextEventAt).getTime() : Infinity) -
          (b.nextEventAt ? new Date(b.nextEventAt).getTime() : Infinity) ||
        a.referenceCode.localeCompare(b.referenceCode),
    );
}

export function OperationsBoard({
  active,
  organizationId,
  canCreate,
  aircraftActive,
  onCreate,
  onOpenOperation,
  onHandoff,
  onExpiredSession,
}: {
  readonly active: boolean;
  readonly organizationId: string;
  readonly canCreate: boolean;
  readonly aircraftActive: number;
  readonly onCreate: () => void;
  readonly onOpenOperation: (id: string) => void;
  readonly onHandoff: () => void;
  readonly onExpiredSession: () => Promise<void>;
}) {
  const [board, setBoard] = useState<ShiftHandoffBoard>();
  const [filter, setFilter] = useState<BoardFilter>('all');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string>();
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<Date>();
  const load = useCallback(
    async (signal: AbortSignal) => {
      const response = await fetch(`/v1/organizations/${organizationId}/shift-handoff`, {
        credentials: 'include',
        signal,
      });
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw new Error('BOARD_UNAVAILABLE');
      return (await response.json()) as ShiftHandoffBoard;
    },
    [organizationId, onExpiredSession],
  );
  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    void load(controller.signal)
      .then((data) => {
        if (controller.signal.aborted || !data) return;
        setBoard(data);
        setUpdatedAt(new Date());
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setError('Não foi possível atualizar as operações. Tente novamente.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [load, revision, active]);
  const operations = board?.operations ?? [];
  const now = new Date();
  const visible = selectBoardOperations(operations, filter, query, now);
  const today = operations.filter(
    (operation) => operation.nextEventAt && isSameLocalDay(operation.nextEventAt, now),
  );
  const attention = operations.filter((operation) => operation.pendingItems.length > 0);
  const critical = operations
    .flatMap((operation) => operation.pendingItems)
    .filter((item) => item.severity === 'critical');
  const running = operations.filter((operation) => operation.state === 'in_progress');
  const upcoming = operations.filter(
    (operation) =>
      operation.nextEventAt && new Date(operation.nextEventAt).getTime() >= now.getTime(),
  );
  const next = selectBoardOperations(upcoming, 'all', '', now)[0];
  const filters: readonly [BoardFilter, string, number][] = [
    ['all', 'Todas', operations.length],
    ['today', 'Hoje', today.length],
    ['attention', 'Com pendências', attention.length],
    ['in_progress', 'Em execução', running.length],
  ];
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <section
      className="operationsBoard"
      id="operations-list"
      aria-labelledby="board-title"
      aria-busy={loading}
    >
      <div className="boardHeading">
        <div>
          <span className="sectionEyebrow">Coordenação de voo</span>
          <h2 id="board-title">Central de operações</h2>
          <p>Da próxima partida ao último registro do turno.</p>
        </div>
        <div className="boardActions">
          <button type="button" onClick={onHandoff}>
            <UiIcon name="users" />
            Passagem de turno
          </button>
          {canCreate ? (
            <button className="primaryButton" type="button" onClick={onCreate}>
              <UiIcon name="plus" />
              Nova operação
            </button>
          ) : null}
        </div>
      </div>
      <div className="boardMetrics" aria-label="Resumo operacional">
        {[
          {
            label: 'Operações abertas',
            value: operations.length,
            detail: 'em acompanhamento',
            icon: 'plane' as const,
          },
          {
            label: 'Próximos horários hoje',
            value: today.length,
            detail: 'no seu fuso horário',
            icon: 'clock' as const,
          },
          {
            label: 'Pendências críticas',
            value: critical.length,
            detail: critical.length ? 'exigem atenção da equipe' : 'nenhuma registrada',
            icon: 'alert' as const,
          },
          {
            label: 'Em execução',
            value: running.length,
            detail: 'operações em curso',
            icon: 'grid' as const,
          },
        ].map((metric) => (
          <article
            className={
              metric.icon === 'alert' && critical.length > 0
                ? 'boardMetric metricAlert'
                : 'boardMetric'
            }
            key={metric.label}
          >
            <div>
              <span>{metric.label}</span>
              <UiIcon name={metric.icon} />
            </div>
            <strong>{board ? metric.value : '—'}</strong>
            <small>{board ? metric.detail : 'Carregando dados…'}</small>
          </article>
        ))}
      </div>
      {next ? (
        <button className="nextOperation" type="button" onClick={() => onOpenOperation(next.id)}>
          <span className="nextIcon">
            <UiIcon name="plane" />
          </span>
          <span>
            <small>Próximo horário planejado</small>
            <strong>
              {next.nextOrigin ?? 'A definir'} <span aria-hidden="true">→</span>{' '}
              {next.nextDestination ?? 'A definir'}
            </strong>
          </span>
          <span className="nextMeta">
            {next.registrationMark ?? 'Sem aeronave'} ·{' '}
            {new Intl.DateTimeFormat('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }).format(new Date(next.nextEventAt!))}
          </span>
          <UiIcon name="arrow" />
        </button>
      ) : null}
      <div className="boardSurface">
        <header className="boardToolbar">
          <div>
            <h3>Voos e operações</h3>
            <p>
              {board
                ? `${operations.length} em acompanhamento · ${aircraftActive} aeronaves ativas`
                : 'Preparando o quadro operacional…'}
            </p>
          </div>
          <button
            className="refreshButton"
            type="button"
            disabled={loading}
            onClick={() => setRevision((value) => value + 1)}
          >
            <UiIcon name="refresh" />
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
        </header>
        {error ? (
          <div className="inlineError" role="alert">
            {error}
            {board ? ' Os dados abaixo são da última atualização.' : ''}
          </div>
        ) : null}
        <div className="boardControls">
          <div className="boardFilters" aria-label="Filtrar operações">
            {filters.map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              >
                {label}
                <span>{board ? count : '—'}</span>
              </button>
            ))}
          </div>
          <label className="boardSearch">
            <UiIcon name="search" />
            <input
              type="search"
              aria-label="Buscar operação, aeronave, rota ou responsável"
              placeholder="Buscar voo, aeronave ou rota"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>
        {!board && loading ? (
          <div className="boardSkeleton" role="status">
            Carregando operações…
            <span />
            <span />
            <span />
          </div>
        ) : null}
        {board && visible.length === 0 ? (
          <div className="boardEmpty">
            <UiIcon name={operations.length ? 'search' : 'plane'} />
            <h3>
              {operations.length ? 'Nenhuma operação encontrada' : 'Seu próximo voo começa aqui'}
            </h3>
            <p>
              {operations.length
                ? 'Tente outra busca ou veja todas as operações.'
                : canCreate
                  ? 'Crie uma operação com a rota, os horários e o responsável pela coordenação.'
                  : 'As operações aparecerão aqui quando forem criadas pela equipe.'}
            </p>
            {operations.length ? (
              <button
                type="button"
                onClick={() => {
                  setFilter('all');
                  setQuery('');
                }}
              >
                Limpar filtros
              </button>
            ) : canCreate ? (
              <button className="primaryButton" type="button" onClick={onCreate}>
                Criar primeira operação
              </button>
            ) : null}
          </div>
        ) : null}
        {visible.length > 0 ? (
          <>
            <div className="flightTableHeading" aria-hidden="true">
              <span>Horário / referência</span>
              <span>Rota / missão</span>
              <span>Aeronave / responsável</span>
              <span>Coordenação</span>
              <span />
            </div>
            <div className="flightRows">
              {visible.map((operation) => {
                const criticalCount = operation.pendingItems.filter(
                  (item) => item.severity === 'critical',
                ).length;
                return (
                  <button
                    className="flightRow"
                    type="button"
                    key={operation.id}
                    onClick={() => onOpenOperation(operation.id)}
                    aria-label={`Abrir operação ${operation.referenceCode}, ${operation.nextOrigin ?? 'origem a definir'} para ${operation.nextDestination ?? 'destino a definir'}`}
                  >
                    <div className="flightTime">
                      <strong>
                        {operation.nextEventAt
                          ? new Intl.DateTimeFormat('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            }).format(new Date(operation.nextEventAt))
                          : 'A definir'}
                      </strong>
                      <span>
                        {operation.nextEventAt
                          ? new Intl.DateTimeFormat('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                            }).format(new Date(operation.nextEventAt))
                          : 'Sem horário'}
                      </span>
                      <small>{operation.referenceCode}</small>
                    </div>
                    <div className="flightRoute">
                      <strong>
                        {operation.nextOrigin ?? 'A definir'}
                        <span aria-hidden="true">→</span>
                        {operation.nextDestination ?? 'A definir'}
                      </strong>
                      <small>{operation.missionName ?? 'Missão sem identificação'}</small>
                    </div>
                    <div className="flightAircraft">
                      <strong>{operation.registrationMark ?? 'Aeronave a definir'}</strong>
                      <small>{operation.responsible?.name ?? 'Responsável a definir'}</small>
                    </div>
                    <div className="flightState">
                      <span className={`operationStatus status-${operation.state}`}>
                        {states[operation.state] ?? operation.state}
                      </span>
                      <small
                        className={
                          criticalCount
                            ? 'criticalLabel'
                            : operation.pendingItems.length
                              ? 'attentionLabel'
                              : 'clearLabel'
                        }
                      >
                        {criticalCount
                          ? `${criticalCount} crítica${criticalCount > 1 ? 's' : ''}`
                          : operation.pendingItems.length
                            ? `${operation.pendingItems.length} pendência${operation.pendingItems.length > 1 ? 's' : ''}`
                            : 'Sem pendências'}
                      </small>
                    </div>
                    <UiIcon className="flightArrow" name="arrow" />
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
        <footer className="boardFooter">
          <span role="status">
            {board
              ? `${visible.length} de ${operations.length} operações`
              : 'Aguardando atualização'}
          </span>
          <span>
            Horários em {timezone.replaceAll('_', ' ')}
            {updatedAt
              ? ` · Atualizado às ${new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(updatedAt)}`
              : ''}
          </span>
        </footer>
      </div>
    </section>
  );
}
